/**
 * Svelte use: action for swipe-to-action on list items (trade cards, selected cards).
 *
 * Usage:
 *   <li use:listswipe={{ onSwipeLeft, onSwipeRight, leftColor, rightColor }}>
 *     <div class="swipe-bg swipe-bg-left"> ... icon ... </div>
 *     <div class="swipe-bg swipe-bg-right"> ... icon ... </div>
 *     <div class="swipe-content"> ... card content ... </div>
 *   </li>
 *
 * The action handles touch events on the .swipe-content element, translating it
 * to reveal the colored background underneath. When the threshold is reached
 * (distance or velocity), it runs a 3-phase animation: slide-out → height-collapse → callback.
 *
 * Options:
 *   onSwipeLeft()  — called when left swipe completes (null = disabled)
 *   onSwipeRight() — called when right swipe completes (null = disabled)
 *   leftColor      — background color for left swipe reveal (default: #ef4444)
 *   rightColor     — background color for right swipe reveal (default: #22c55e)
 */

const SWIPE_THRESHOLD = 80;
const RESISTANCE = 0.5;
const MIN_VELOCITY = 0.5; // px/ms
const MIN_VELOCITY_DISTANCE = 40; // px
const SLIDE_DURATION = 200; // ms
const COLLAPSE_DURATION = 300; // ms

/**
 * @typedef {Object} ListSwipeOptions
 * @property {(() => void) | null} [onSwipeLeft] - null/absent disables left swipe
 * @property {(() => void) | null} [onSwipeRight] - null/absent disables right swipe
 * @property {string} [leftColor]
 * @property {string} [rightColor]
 */

/**
 * @param {HTMLElement} node
 * @param {ListSwipeOptions} [options]
 */
export function listswipe(node, options = {}) {
  let opts = { ...options };
  let content = null;
  let bgLeft = null;
  let bgRight = null;

  let startX = 0;
  let startY = 0;
  let startTime = 0;
  let currentX = 0;
  let tracking = false;
  let directionLocked = false; // true once we've committed to horizontal or vertical
  let isHorizontal = false;

  function findElements() {
    content = node.querySelector('.swipe-content');
    bgLeft = node.querySelector('.swipe-bg-left');
    bgRight = node.querySelector('.swipe-bg-right');
  }

  function onTouchStart(e) {
    if (e.touches.length !== 1) return;
    findElements();
    if (!content) return;

    const touch = e.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
    startTime = Date.now();
    currentX = 0;
    tracking = true;
    directionLocked = false;
    isHorizontal = false;

    // Remove any leftover transition
    content.style.transition = 'none';
    content.style.willChange = 'transform';
  }

  function onTouchMove(e) {
    if (!tracking || !content) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;

    // First significant movement determines scroll vs swipe
    if (!directionLocked) {
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);
      if (absX < 10 && absY < 10) return; // deadzone

      directionLocked = true;
      if (absY > absX) {
        // Vertical scroll — abort swipe
        tracking = false;
        content.style.willChange = '';
        return;
      }
      isHorizontal = true;
    }

    if (!isHorizontal) return;
    e.preventDefault();

    // Determine if this direction has an action
    const goingLeft = deltaX < 0;
    const hasAction = goingLeft ? !!opts.onSwipeLeft : !!opts.onSwipeRight;

    let translateX;
    if (!hasAction) {
      // Heavy resistance — no valid action in this direction
      translateX = deltaX * 0.2;
    } else if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      // Past threshold: apply resistance
      const excess = Math.abs(deltaX) - SWIPE_THRESHOLD;
      translateX = (SWIPE_THRESHOLD + excess * RESISTANCE) * Math.sign(deltaX);
    } else {
      translateX = deltaX;
    }

    currentX = deltaX;
    content.style.transform = `translateX(${translateX}px)`;

    // Show/hide background layers
    if (bgLeft) bgLeft.style.display = deltaX < -10 ? 'flex' : 'none';
    if (bgRight) bgRight.style.display = deltaX > 10 ? 'flex' : 'none';
  }

  function onTouchEnd() {
    if (!tracking || !content) return;
    tracking = false;
    content.style.willChange = '';

    const deltaX = currentX;
    const absX = Math.abs(deltaX);
    const elapsed = Date.now() - startTime;
    const velocity = elapsed > 0 ? absX / elapsed : 0;

    const goingLeft = deltaX < 0;
    const callback = goingLeft ? opts.onSwipeLeft : opts.onSwipeRight;

    // Check if swipe triggers: threshold distance OR velocity-based
    const triggered =
      callback &&
      (absX >= SWIPE_THRESHOLD || (velocity >= MIN_VELOCITY && absX >= MIN_VELOCITY_DISTANCE));

    if (triggered) {
      animateOut(goingLeft, callback);
    } else {
      // Snap back
      content.style.transition = `transform ${SLIDE_DURATION}ms ease-out`;
      content.style.transform = 'translateX(0)';
      setTimeout(() => {
        if (content) {
          content.style.transition = '';
          content.style.transform = '';
        }
        if (bgLeft) bgLeft.style.display = 'none';
        if (bgRight) bgRight.style.display = 'none';
      }, SLIDE_DURATION);
    }
  }

  function animateOut(goingLeft, callback) {
    const width = node.offsetWidth;
    const slideTarget = goingLeft ? -width : width;

    // Phase 1: Slide out
    content.style.transition = `transform ${SLIDE_DURATION}ms ease-out`;
    content.style.transform = `translateX(${slideTarget}px)`;

    setTimeout(() => {
      // Phase 2: Height collapse
      // Lock current height as starting point
      const currentHeight = node.offsetHeight;
      node.style.height = `${currentHeight}px`;
      node.style.minHeight = '0'; // override CSS min-height: 44px
      node.style.overflow = 'hidden';

      // Zero inner content so it doesn't resist the collapse
      if (content) {
        content.style.minHeight = '0';
        content.style.height = '0';
        content.style.padding = '0';
        content.style.border = 'none';
        content.style.overflow = 'hidden';
      }

      // Force reflow so browser registers starting height
      void node.offsetHeight;

      node.style.transition = `height ${COLLAPSE_DURATION}ms ease-out`;
      node.style.height = '0px';

      setTimeout(() => {
        // Phase 3: Perform action
        callback();
      }, COLLAPSE_DURATION);
    }, SLIDE_DURATION);
  }

  node.addEventListener('touchstart', onTouchStart, { passive: true });
  node.addEventListener('touchmove', onTouchMove, { passive: false });
  node.addEventListener('touchend', onTouchEnd, { passive: true });

  return {
    update(newOptions) {
      opts = { ...newOptions };
    },
    destroy() {
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
    },
  };
}
